import { Link } from '@tanstack/react-router';
import { itemsHref } from '@/routes/items/-search-params';

interface ValueTag {
  id: number;
  name: string;
}

interface KeyTag {
  id: number;
  name: string;
}

interface ItemTag {
  keyTags: KeyTag | null;
  valueTags: ValueTag;
}

interface MergedTag {
  keyName: string;
  values: ValueTag[];
}

interface TagsProps {
  itemTags: ItemTag[];
}

export default function Tags({ itemTags }: TagsProps) {
  const tagsMerged: Record<number, MergedTag> = {};

  for (const tag of itemTags) {
    if (!tag.keyTags || !tag.valueTags) continue;
    const keyId = tag.keyTags.id;
    if (tagsMerged[keyId]) {
      tagsMerged[keyId].values.push(tag.valueTags);
    } else {
      tagsMerged[keyId] = {
        keyName: tag.keyTags.name,
        values: [tag.valueTags],
      };
    }
  }

  const mergedTagsArray = Object.values(tagsMerged);
  const allTagIds = mergedTagsArray.flatMap(({ values }) => values.map(({ id }) => id));

  if (mergedTagsArray.length === 0) return null;

  return (
    <div className="tags-container mb-3 flex w-full flex-wrap gap-2">
      {mergedTagsArray.map(({ values, keyName }) => {
        const filterIds = values.map(({ id }) => id);
        const valueNames = values.map(({ name }) => name).join(', ');

        return (
          <Link
            key={`${keyName}-${filterIds[0]}`}
            to={itemsHref({ filters: filterIds })}
            className="tag-link group"
          >
            <div className="tag-badge flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-3 py-2 transition-all duration-300 hover:-translate-y-px hover:border-blue-300 hover:from-blue-50 hover:to-blue-100 hover:shadow-lg hover:shadow-blue-500/20 dark:border-slate-600 dark:from-slate-800 dark:to-slate-700 dark:hover:border-blue-500/50 dark:hover:from-blue-900/30 dark:hover:to-blue-800/20 dark:hover:shadow-blue-400/30">
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-slate-600 transition-colors duration-300 group-hover:text-blue-700 dark:text-slate-400 dark:group-hover:text-blue-300">
                  {keyName}:
                </span>
                <span className="text-xs font-medium text-slate-700 transition-colors duration-300 group-hover:text-blue-800 dark:text-slate-300 dark:group-hover:text-blue-200">
                  {valueNames}
                </span>
              </div>
              <div className="tag-icon flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 transition-all duration-300 group-hover:bg-blue-100 group-hover:shadow-sm dark:bg-slate-600 dark:group-hover:bg-blue-900/50">
                <svg
                  aria-hidden="true"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  className="text-slate-500 transition-colors duration-300 group-hover:text-blue-600 dark:text-slate-400 dark:group-hover:text-blue-400"
                >
                  <path
                    d="M2 5L4 7L8 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </Link>
        );
      })}

      {mergedTagsArray.length > 1 && (
        <Link to={itemsHref({ filters: allTagIds })} className="tag-link group">
          <div className="tag-badge flex cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50 px-3 py-2 transition-all duration-300 hover:-translate-y-px hover:border-blue-400 hover:bg-blue-100/50 hover:shadow-lg hover:shadow-blue-500/20 dark:border-blue-600 dark:bg-blue-900/20 dark:hover:border-blue-500 dark:hover:bg-blue-900/30 dark:hover:shadow-blue-400/30">
            <span className="text-xs font-medium text-blue-700 transition-colors duration-300 group-hover:text-blue-800 dark:text-blue-300 dark:group-hover:text-blue-200">
              {
                '\u05db\u05dc \u05d4\u05de\u05d5\u05e6\u05e8\u05d9\u05dd \u05e2\u05dd \u05ea\u05d2\u05d9\u05d5\u05ea \u05d4\u05de\u05d5\u05e6\u05e8'
              }
            </span>
            <div className="tag-icon flex h-4 w-4 items-center justify-center rounded-full bg-blue-200 transition-all duration-300 group-hover:bg-blue-300 dark:bg-blue-700 dark:group-hover:bg-blue-600">
              <svg
                aria-hidden="true"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="text-blue-600 transition-colors duration-300 dark:text-blue-300"
              >
                <path
                  d="M2 5h6M5 2v6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}
